import { useCallback, useEffect, useRef } from 'react';
import { useStore } from '../../store/store';
import {
  setQsgSearchResults,
  setQsgSearchError,
  setQsgSearchLoading,
} from '../../store/slices/qsg/actions';
import { getQSGSearchResults } from '../../fetch-data/graphql/helpers';
import { searchAndPaginateResults } from '../../utils/search';

export const SEARCH_LIMIT = 10;

export const getSearchKey = (searchTerm: string, page: number): string => {
  return `${searchTerm}:${page}`;
};

export const useSearchLoader = (client: any, hasInit: boolean) => {
  const loadingSearchesRef = useRef<Set<string>>(new Set());
  const [state, dispatch] = useStore();

  const {
    qsgContent,
    qsgSearchTerm,
    qsgSearchPage,
    qsgSearchResultsMap,
    qsgSearchErrors,
    selectedModel,
  } = state.qsg;

  const loadSearchResults = useCallback(
    async (searchTerm: string, page: number) => {
      const searchKey = getSearchKey(searchTerm, page);

      if (loadingSearchesRef.current.has(searchKey)) return;

      loadingSearchesRef.current.add(searchKey);

      dispatch(setQsgSearchLoading(searchKey, true));
      dispatch(setQsgSearchError(searchKey, null));

      try {
        let data;

        if (qsgContent)
          data = searchAndPaginateResults(
            qsgContent,
            searchTerm,
            page,
            SEARCH_LIMIT
          );

        if (!data)
          data = await getQSGSearchResults(
            selectedModel.modelYear,
            selectedModel.modelId,
            searchTerm,
            page,
            SEARCH_LIMIT,
            client
          );

        if (data) {
          dispatch(setQsgSearchResults(searchKey, data));
        } else {
          dispatch(setQsgSearchError(searchKey, 'No search results found'));
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load search results';
        dispatch(setQsgSearchError(searchKey, errorMessage));
      } finally {
        loadingSearchesRef.current.delete(searchKey);
      }
    },
    [
      client,
      dispatch,
      qsgContent,
      selectedModel.modelYear,
      selectedModel.modelId,
    ]
  );

  useEffect(() => {
    if (!hasInit) return;
    if (!qsgSearchTerm.trim()) return;
    if (!selectedModel.modelId || !selectedModel.modelYear) return;

    const searchKey = getSearchKey(qsgSearchTerm, qsgSearchPage);

    const shouldLoadSearch =
      !qsgSearchResultsMap[searchKey] &&
      !loadingSearchesRef.current.has(searchKey) &&
      !qsgSearchErrors[searchKey];

    if (shouldLoadSearch) {
      loadSearchResults(qsgSearchTerm, qsgSearchPage);
    }
  }, [
    hasInit,
    qsgSearchTerm,
    qsgSearchPage,
    qsgSearchResultsMap,
    qsgSearchErrors,
    selectedModel.modelId,
    selectedModel.modelYear,
    loadSearchResults,
  ]);

  return {
    loadSearchResults,
  };
};
